var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Cache } from './cache';
function waitFor(milliseconds) {
    return __awaiter(this, void 0, void 0, function* () {
        return new Promise((resolve) => {
            setTimeout(resolve, milliseconds);
        });
    });
}
function fetchRetry(input, init) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        function wrapFetch(retries) {
            return __awaiter(this, void 0, void 0, function* () {
                try {
                    if (init === null || init === void 0 ? void 0 : init.signal) {
                        init.signal.throwIfAborted();
                    }
                    return fetch(input, init);
                }
                catch (error) {
                    const isUnrecoverableError = error instanceof Error && error.name === 'AbortError';
                    if (isUnrecoverableError) {
                        throw error;
                    }
                    if (retries <= 1) {
                        throw error;
                    }
                    yield waitFor(100);
                    return wrapFetch(retries - 1);
                }
            });
        }
        return wrapFetch((_a = init === null || init === void 0 ? void 0 : init.retries) !== null && _a !== void 0 ? _a : 2);
    });
}
class AuthClient {
    constructor(endpoints) {
        this.endpoints = endpoints;
        const oneSecond = 1000;
        const cacheTtl = 5 * oneSecond;
        this.userInfoCache = new Cache(cacheTtl);
    }
    getUserInfo() {
        return __awaiter(this, void 0, void 0, function* () {
            const cachedUserInfo = this.userInfoCache.get();
            if (cachedUserInfo) {
                return cachedUserInfo;
            }
            const url = new URL(this.endpoints.userinfo, window.location.origin);
            try {
                const response = yield fetchRetry(url, { method: 'GET', retries: 3 });
                if (response.ok) {
                    const userInfo = (yield response.json());
                    this.userInfoCache.set(userInfo);
                    return userInfo;
                }
                return null;
            }
            catch (_a) {
                return null;
            }
        });
    }
    verifySession(signal) {
        return __awaiter(this, void 0, void 0, function* () {
            const url = new URL(this.endpoints.verify, window.location.origin);
            try {
                const response = yield fetchRetry(url, {
                    method: 'HEAD',
                    signal,
                    retries: 3,
                });
                return response.status === 202;
            }
            catch (_a) {
                return false;
            }
        });
    }
    /**
     * Builds the login URL for authentication, handling both standard OIDC and Auth0-specific parameters.
     *
     * Standard OIDC query parameters (like client_id, response_type, scope, etc.) are automatically
     * handled by the oauth2-proxy. This method specifically handles Auth0-specific query parameters
     * that need to be passed through to the Auth0 authorization endpoint.
     *
     * Note: For Auth0-specific parameters to work, they must first be configured in the oauth2-proxy
     * to be allowed to pass through to the Auth0 authorization endpoint.
     *
     * Additionally handles:
     * - returnTo URL via the 'rd' parameter for post-login redirection
     *
     * @param options RedirectLoginOptions - Optional parameters for customizing the login behavior
     * @returns string - The complete login URL with all configured parameters
     */
    buildLoginURL(options) {
        var _a;
        const url = new URL(this.endpoints.login, window.location.origin);
        // Auth0 specific query parameters for the /authorize endpoint
        const parameters = ['prompt', 'ui_locales', 'screen_hint', 'connection', 'organization'];
        parameters.forEach((parameter) => {
            if ((options === null || options === void 0 ? void 0 : options[parameter]) !== undefined) {
                url.searchParams.set(parameter, options[parameter]);
            }
        });
        if ((_a = options === null || options === void 0 ? void 0 : options.appState) === null || _a === void 0 ? void 0 : _a.returnTo) {
            url.searchParams.set('rd', options.appState.returnTo);
        }
        return url;
    }
    loginWithRedirect(options) {
        window.location.assign(this.buildLoginURL(options));
    }
    logout() {
        window.location.assign(new URL(this.endpoints.logout, window.location.origin));
    }
}
export const auth = new AuthClient({
    login: process.env.AUTH_ENDPOINT_LOGIN,
    logout: process.env.AUTH_ENDPOINT_LOGOUT,
    userinfo: process.env.AUTH_ENDPOINT_USERINFO,
    verify: process.env.AUTH_ENDPOINT_VERIFY,
});
